import { useMemo, useState } from 'react'
import { Download, FileSpreadsheet, FileText, ShieldAlert } from 'lucide-react'
import { endOfMonth, startOfMonth } from 'date-fns'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { PermissionGate } from '@/components/common/PermissionGate'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { Card, Button } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { usePagedListParams } from '@/hooks/usePagedListParams'
import { usePermissions } from '@/hooks/usePermissions'
import {
  getComplianceReportSummaryFilters,
} from '@/api/services/reports.service'
import { useWorkspaceCompanyFilter } from '@/features/companies/components/WorkspaceCompanyFilter'
import { ComplianceReportDetailModal } from './ComplianceReportDetailModal'
import { ComplianceReportFilters } from './ComplianceReportFilters'
import { ComplianceReportTable } from './ComplianceReportTable'
import {
  useComplianceReports,
  useComplianceReportSummary,
  useExportComplianceReports,
} from '@/features/reports/hooks/useReports'
import type {
  ComplianceReportQuery,
  ComplianceReportRow,
} from '@/features/reports/types/compliance-report.types'

const DEFAULT_LIMIT = 10
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

function monthStartIso(): string {
  const value = startOfMonth(new Date())
  return `${value.toISOString().slice(0, 10)}T00:00:00.000Z`
}

function monthEndIso(): string {
  const value = endOfMonth(new Date())
  return `${value.toISOString().slice(0, 10)}T23:59:59.999Z`
}

function summaryCard(
  label: string,
  value: React.ReactNode,
  tone?: 'default' | 'danger'
): React.ReactElement {
  return (
    <div
      className={[
        'rounded-xl border p-4 shadow-sm',
        tone === 'danger'
          ? 'border-red-200 bg-red-50/70 dark:border-red-900/60 dark:bg-red-950/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
      ].join(' ')}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold text-gray-950 dark:text-gray-100">{value}</p>
    </div>
  )
}

export function ReportsPage(): React.ReactElement {
  const { can, isLoading: permissionsLoading } = usePermissions()
  const { params, setPage, setLimit, applyFilters, reset } = usePagedListParams<ComplianceReportQuery>({
    page: 1,
    limit: DEFAULT_LIMIT,
    sortBy: 'complianceScore',
    sortOrder: 'desc',
    dateFrom: monthStartIso(),
    dateTo: monthEndIso(),
  })
  const companyFilter = useWorkspaceCompanyFilter({
    onChange: () => setPage(1),
  })
  const [selectedReport, setSelectedReport] = useState<ComplianceReportRow | null>(null)

  const listQuery = useMemo(
    () => ({
      ...params,
      companyIds: companyFilter.companyIds,
    }),
    [companyFilter.companyIds, params]
  )
  const summaryFilters = useMemo(
    () => getComplianceReportSummaryFilters(listQuery),
    [listQuery]
  )

  const listQueryResult = useComplianceReports(listQuery)
  const summaryQueryResult = useComplianceReportSummary(summaryFilters)
  const exportCsv = useExportComplianceReports('csv')
  const exportExcel = useExportComplianceReports('excel')

  const listData = listQueryResult.data
  const summaryData = summaryQueryResult.data?.data ?? listData?.summary
  const reports = listData?.data ?? []
  const total = listData?.total ?? 0
  const page = listData?.page ?? params.page ?? 1
  const limit = listData?.limit ?? params.limit ?? DEFAULT_LIMIT
  const totalPages = listData?.totalPages ?? 0
  const canReadReports = can('reports', 'read')

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Reports' },
      ]}
      icon={ShieldAlert}
      title="Reports"
      description="Track compliance health across companies with one shared reporting workspace."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <>
              <PermissionGate module="reports" action="export">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => exportCsv.mutate(summaryFilters)}
                  loading={exportCsv.isPending}
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="ml-1.5">Export CSV</span>
                </Button>
              </PermissionGate>
              <PermissionGate module="reports" action="export">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => exportExcel.mutate(summaryFilters)}
                  loading={exportExcel.isPending}
                >
                  <FileSpreadsheet className="h-4 w-4 shrink-0" />
                  <span className="ml-1.5">
                    Export Excel
                  </span>
                </Button>
              </PermissionGate>
            </>
          }
        />

        {summaryQueryResult.isLoading && !summaryData ? (
          <SectionLoader variant="cards" rows={4} label="Loading report summary" />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {summaryCard('Total companies', summaryData?.totalCompanies ?? 0)}
            {summaryCard(
              'Average score',
              Number(summaryData?.averageComplianceScore ?? 0).toFixed(2)
            )}
            {summaryCard('Pending approvals', summaryData?.pendingApprovals ?? 0)}
            {summaryCard('Open issues', summaryData?.totalOpenIssues ?? 0, 'danger')}
          </div>
        )}

        <Card
          className="border-gray-200 bg-gray-50/80 dark:border-gray-700 dark:bg-gray-800/50"
          header={
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Filters
            </span>
          }
        >
          <ComplianceReportFilters
            params={listQuery}
            companyFilter={companyFilter}
            onApply={(next) => applyFilters(next)}
            onReset={() =>
              reset()
            }
          />
        </Card>

        <Card
          contentClassName="p-0"
          header={
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Compliance overview
              </span>
              {!listQueryResult.isLoading && total > 0 ? (
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {total} companies
                </span>
              ) : null}
            </div>
          }
          footer={
            !listQueryResult.isLoading && !listQueryResult.error && total > 0 ? (
              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                onLimitChange={setLimit}
              />
            ) : undefined
          }
        >
          <ComplianceReportTable
            reports={reports}
            isLoading={listQueryResult.isLoading || permissionsLoading}
            error={listQueryResult.error}
            hasAccess={canReadReports}
            onRetry={() => void listQueryResult.refetch()}
            onViewDetails={setSelectedReport}
          />
        </Card>

        <ComplianceReportDetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      </div>
    </PageShell>
  )
}

ReportsPage.displayName = 'ReportsPage'
